import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { listBookings } from '../api/endpoints/bookings';
import { listSessions } from '../api/endpoints/sessions';
import type { BusinessDetail } from '../api/schemas';
import { isCoach } from '../auth/roles';
import { useSession } from '../auth/useSession';
import { ErrorBanner } from '../components/ErrorBanner';
import { blockStyle, PX_PER_MINUTE, SHORT_BLOCK_MIN } from '../components/calendarBlock';
import { ClassCalendarEvent } from '../components/ClassCalendarEvent';
import { LoadingState } from '../components/LoadingState';
import { PageHeader } from '../components/PageHeader';
import { customerNameOf } from '../lib/bookingPresentation';
import { bookingsQueryKey, dayWindow } from '../lib/bookingWindows';
import { buildCalendarDay, shortClock, type CalendarBlock } from '../lib/calendarDay';
import { buildClassDay } from '../lib/classCalendarDay';
import { initialOf } from '../lib/customers';
import { isSameDay } from '../lib/format';
import { modeForCategory } from '../lib/mode';
import { serviceName, useBusiness } from '../lib/useBusiness';

const REFETCH_INTERVAL_MS = 30_000;
/** Service colors, in catalog order: color follows the service, never its rank. */
const SERVICE_TONES = 6;

/**
 * The day at a glance, a column per barber: who sits in which chair, when.
 * A barber whose login is linked to their chair sees only that column; the
 * API narrows their bookings the same way.
 */
export function CalendarioPage() {
  const { session } = useSession();
  const business = useBusiness();
  const [day, setDay] = useState(() => startOfDay(new Date()));
  const range = dayWindow(day);
  // A studio teaches classes, so its calendar reads the classes, not each booking in them.
  const isStudio = business ? modeForCategory(business.category) === 'classes' : false;
  const bookingsQuery = useQuery({
    queryKey: bookingsQueryKey(range),
    queryFn: () => listBookings(range),
    enabled: !isStudio,
    refetchInterval: REFETCH_INTERVAL_MS,
    // Stepping to the next day keeps the grid up while that day loads.
    placeholderData: keepPreviousData,
  });
  const sessionsQuery = useQuery({
    queryKey: ['calendar-sessions', range.from, range.to],
    queryFn: () => listSessions(range.from, range.to),
    enabled: isStudio,
    refetchInterval: REFETCH_INTERVAL_MS,
    placeholderData: keepPreviousData,
  });
  const activeQuery = isStudio ? sessionsQuery : bookingsQuery;

  const onlyStaffId = isCoach(session) ? session?.staffMemberId ?? undefined : undefined;
  const calendar = useMemo(
    () =>
      business && !isStudio && bookingsQuery.data
        ? buildCalendarDay(bookingsQuery.data.data, business.staff, day, onlyStaffId)
        : undefined,
    [business, isStudio, bookingsQuery.data, day, onlyStaffId],
  );
  const classDay = useMemo(
    () =>
      business && isStudio && sessionsQuery.data
        ? buildClassDay(sessionsQuery.data.data, business.staff, day, onlyStaffId)
        : undefined,
    [business, isStudio, sessionsQuery.data, day, onlyStaffId],
  );
  const noun = isStudio ? CLASS_NOUN : VISIT_NOUN;
  const shown = classDay ?? calendar;
  const toneOf = useMemo(() => serviceTones(business), [business]);
  const isToday = isSameDay(day, new Date());

  return (
    <main className="cal-page">
      <PageHeader title={onlyStaffId ? 'Mi calendario' : 'Calendario'} description={pageDescription(isStudio, Boolean(onlyStaffId))} />
      <div className="cal-toolbar">
        <button type="button" className="btn small secondary" disabled={isToday} onClick={() => setDay(startOfDay(new Date()))}>
          Hoy
        </button>
        <div className="cal-stepper">
          <button type="button" className="cal-step" aria-label="Día anterior" onClick={() => setDay(addDays(day, -1))}>
            <Chevron direction="left" />
          </button>
          <button type="button" className="cal-step" aria-label="Día siguiente" onClick={() => setDay(addDays(day, 1))}>
            <Chevron direction="right" />
          </button>
        </div>
        <p className="cal-date">{dayLabel(day)}</p>
        {shown ? <p className="row-sub cal-count">{countLabel(shown.count, noun)}</p> : null}
      </div>

      {activeQuery.isLoading ? <LoadingState label="Cargando el calendario…" /> : null}
      {activeQuery.isError ? <ErrorBanner message={messageFor(activeQuery.error)} /> : null}
      {calendar ? (
        <CalendarGrid
          calendar={calendar}
          noun={noun}
          isToday={isToday}
          renderBlock={(block) => (
            <CalendarEvent key={block.booking.id} block={block} business={business} tone={toneOf(block.booking.serviceId)} />
          )}
        />
      ) : null}
      {classDay ? (
        <CalendarGrid
          calendar={classDay}
          noun={noun}
          isToday={isToday}
          renderBlock={(block) => (
            <ClassCalendarEvent key={block.item.id} block={block} business={business} tone={toneOf(block.item.serviceId)} />
          )}
        />
      ) : null}
    </main>
  );
}

type Noun = { one: string; many: string };
const VISIT_NOUN: Noun = { one: 'cita', many: 'citas' };
const CLASS_NOUN: Noun = { one: 'clase', many: 'clases' };

type GridDay<Block> = { startHour: number; endHour: number; columns: { id: string; name: string; blocks: Block[] }[] };

type CalendarGridProps<Block> = {
  calendar: GridDay<Block>;
  noun: Noun;
  isToday: boolean;
  renderBlock: (block: Block) => ReactNode;
};

function CalendarGrid<Block extends { startMin: number }>({ calendar, noun, isToday, renderBlock }: CalendarGridProps<Block>) {
  const scroller = useRef<HTMLDivElement>(null);
  const hours = Array.from({ length: calendar.endHour - calendar.startHour }, (_, index) => calendar.startHour + index);
  const bodyHeight = hours.length * 60 * PX_PER_MINUTE;
  const nowMin = useNowMinute();
  const nowOffset = nowMin - calendar.startHour * 60;
  const showNow = isToday && nowOffset >= 0 && nowOffset <= hours.length * 60;

  // Open on the hour that matters: now today, the first visit any other day.
  useEffect(() => {
    const firstVisit = Math.min(...calendar.columns.flatMap((column) => column.blocks.map((block) => block.startMin)));
    const target = showNow ? nowOffset - 60 : Number.isFinite(firstVisit) ? firstVisit - 30 : 60;
    scroller.current?.scrollTo({ top: Math.max(target, 0) * PX_PER_MINUTE });
    // Only when the day changes, not on every refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendar.startHour, isToday, calendar.columns.length]);

  const gridStyle = { '--cal-columns': calendar.columns.length } as CSSProperties;
  return (
    <div className="cal" style={gridStyle}>
      <div className="cal-scroll" ref={scroller}>
        <div className="cal-head">
          <span className="cal-corner" aria-hidden="true" />
          {calendar.columns.map((column, index) => (
            <div key={column.id} className="cal-person">
              <span className="cal-avatar" data-tone={index % SERVICE_TONES}>
                {initialOf(column.name)}
              </span>
              <span className="cal-person-name">{column.name}</span>
              <span className="cal-person-count">{countLabel(column.blocks.length, noun)}</span>
            </div>
          ))}
        </div>
        <div className="cal-body" style={{ height: bodyHeight }}>
          <div className="cal-rail" aria-hidden="true">
            {hours.map((hour) => (
              <span key={hour} className="cal-hour" style={{ top: (hour - calendar.startHour) * 60 * PX_PER_MINUTE }}>
                {hourLabel(hour)}
              </span>
            ))}
          </div>
          {calendar.columns.map((column) => (
            <div key={column.id} className="cal-column" aria-label={`Citas de ${column.name}`}>
              {column.blocks.map(renderBlock)}
            </div>
          ))}
          {showNow ? (
            <div className="cal-now" style={{ top: nowOffset * PX_PER_MINUTE }} aria-hidden="true">
              <span className="cal-now-dot" />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function CalendarEvent({ block, business, tone }: { block: CalendarBlock; business: BusinessDetail | undefined; tone: number }) {
  const { booking } = block;
  const minutes = block.endMin - block.startMin;
  const starts = new Date(booking.startsAt);
  const offsetStart = starts.getHours() * 60 + starts.getMinutes();
  const name = customerNameOf(booking) ?? 'Sin nombre';
  const style = blockStyle(block);
  const range = `${shortClock(offsetStart)} a ${shortClock(offsetStart + minutes)}`;
  return (
    <Link
      to="/reservas/$bookingId"
      params={{ bookingId: booking.id }}
      className="cal-event"
      data-tone={tone}
      data-status={booking.status}
      data-short={minutes < SHORT_BLOCK_MIN || undefined}
      style={style}
      aria-label={`${range}, ${name}, ${serviceName(business, booking.serviceId)}`}
    >
      <span className="cal-event-time">{range}</span>
      <span className="cal-event-name">{name}</span>
      <span className="cal-event-service">{serviceName(business, booking.serviceId)}</span>
    </Link>
  );
}

function useNowMinute(): number {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  return now.getHours() * 60 + now.getMinutes();
}

function serviceTones(business: BusinessDetail | undefined): (serviceId: string) => number {
  const order = new Map((business?.services ?? []).map((service, index) => [service.id, index % SERVICE_TONES]));
  return (serviceId) => order.get(serviceId) ?? 0;
}

function Chevron({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path
        d={direction === 'left' ? 'M10 3.5 5.5 8 10 12.5' : 'M6 3.5 10.5 8 6 12.5'}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function countLabel(count: number, noun: Noun): string {
  return `${count} ${count === 1 ? noun.one : noun.many}`;
}

function pageDescription(isStudio: boolean, isOwnDay: boolean): string {
  if (isOwnDay) return 'Tu día, cita por cita.';
  return isStudio ? 'Las clases del día, coach por coach.' : 'El día de todo tu equipo, de un vistazo.';
}

function hourLabel(hour: number): string {
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelve} ${hour < 12 ? 'a. m.' : 'p. m.'}`;
}

function dayLabel(day: Date): string {
  const text = day.toLocaleDateString('es-PA', { weekday: 'long', day: 'numeric', month: 'long' });
  return text.charAt(0).toUpperCase() + text.slice(1).replace(',', '');
}

function startOfDay(date: Date): Date {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return start;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function messageFor(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos cargar el calendario. Intenta de nuevo.';
}
